import { useCallback, useRef, useState } from 'react'

import { Flex } from '../../../../Components'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import {
  AddServiceButton,
  ServiceEmptyState,
  SingleServiceContainer,
  TransferField
} from '../Components'
import {
  addService,
  getElementsByService,
  getServiceCount,
  readKey,
  removeService
} from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { buildCarServiceSchema } from './Car.schema'
import { useMultiServiceZodValidation } from './multiServiceValidation'
const language = cap.language.state || 'IT'
const t = translations[language]

const {
  car: {
    pickupPlace,
    pickupDate,
    pickupHour,
    dropoffPlace,
    dropoffDate,
    dropoffHour,
    carCategory,
    carType,
    carTransmission,
    noteAggiuntive = ''
  }
} = getElementsByService()

const SECTION_ID = 'car'

export const CarSection = ({ state: [state, setState], readOnly }) => {
  const [valTick, setValTick] = useState(0)
  const trackingSetState = (data, forceRender) => {
    setState(data, forceRender)
    setValTick((v) => v + 1)
  }
  const stateProp = [state, trackingSetState]
  const statePropRender = [state, (data) => trackingSetState(data, true)]
  const containerRef = useRef(null)
  const { sectionHasErrors } = useGigiFormValidation()

  const carCount = getServiceCount(state, 'car')

  const buildSchema = useCallback(() => buildCarServiceSchema(), [])
  const buildServiceValues = useCallback(
    (formState, i) => ({
      pickupPlace: readKey(formState, pickupPlace.location, i) ?? '',
      pickupDate: readKey(formState, pickupDate.location, i) ?? '',
      pickupHour: readKey(formState, pickupHour.location, i) ?? '',
      dropoffPlace: readKey(formState, dropoffPlace.location, i) ?? '',
      dropoffDate: readKey(formState, dropoffDate.location, i) ?? '',
      dropoffHour: readKey(formState, dropoffHour.location, i) ?? '',
      carCategory: readKey(formState, carCategory.location, i) ?? '',
      carType: readKey(formState, carType.location, i) ?? '',
      carTransmission: readKey(formState, carTransmission.location, i) ?? '',
      noteAggiuntive: readKey(formState, noteAggiuntive.location, i) ?? ''
    }),
    []
  )

  useMultiServiceZodValidation({
    sectionId: SECTION_ID,
    containerRef,
    state,
    serviceCount: carCount,
    buildSchema,
    buildServiceValues,
    trigger: valTick
  })

  return (
    <SectionShell
      id="car"
      icon="car"
      title={t.serviziAuto?.toUpperCase() || 'AUTO'}
      storageKey="gigiForm.section.car"
      hasErrors={sectionHasErrors('car')}
      count={carCount}
      contentId="car-content"
      headerExtra={
        !readOnly && (
          <AddServiceButton
            label={t.aggiungiServizio}
            onClick={async () => setState(await addService(state, 'car'))}
          />
        )
      }
    >
      <div ref={containerRef}>
        <Flex fw>
          {!carCount && (
            <ServiceEmptyState
              icon="car"
              title={t.nessunServizioAutoInserito}
              hint={t.servizioEmptyHint}
            />
          )}
          {Array(getServiceCount(state, 'car'))
            .fill()
            .map((_, i) => {
              const departureDateValue = readKey(state, pickupDate?.location, i)
              const minDateArrival = departureDateValue
                ? new Date(
                    new Date(
                      departureDateValue.split('/')[2],
                      departureDateValue.split('/')[1] - 1,
                      departureDateValue.split('/')[0]
                    )
                  )
                : new Date()
              return (
                <SingleServiceContainer
                  key={i}
                  index={i}
                  serviceType="car"
                  state={state}
                  onRemove={() => setState(removeService(state, 'car', i))}
                  readOnly={readOnly}
                >
                  <TransferField
                    state={statePropRender}
                    item={pickupDate}
                    key="pickupDate"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={{ ...dropoffDate, minDate: minDateArrival }}
                    key="dropoffDate"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={pickupPlace}
                    key="pickupPlace"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={pickupHour}
                    key="pickupHour"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={dropoffPlace}
                    key="dropoffPlace"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={dropoffHour}
                    key="dropoffHour"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={carCategory}
                    key="carCategory"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={carType}
                    key="carType"
                    count={i}
                    readOnly={readOnly}
                  />
                  <TransferField
                    state={stateProp}
                    item={carTransmission}
                    key="carTransmission"
                    count={i}
                    readOnly={readOnly}
                  />
                  <div aria-hidden className="ff-service-grid--break" />
                  <div className="ff-service-grid--full">
                    <TransferField
                      state={stateProp}
                      item={noteAggiuntive}
                      key="noteAggiuntive"
                      count={i}
                      readOnly={readOnly}
                    />
                  </div>
                </SingleServiceContainer>
              )
            })}
        </Flex>
      </div>
    </SectionShell>
  )
}
